import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Role } from 'src/app/classes/auth/role.class';
import { User } from 'src/app/classes/user.class';

export interface MentionItem {
    type: 'user' | 'role';
    id?: number;
    name: string;
    email?: string;
    role?: Role;
}

@Component({
    selector: 'app-user-mention',
    standalone: true,
    imports: [],
    templateUrl: './user-mention.component.html',
    styleUrl: './user-mention.component.scss',
})
export class UserMentionComponent implements OnInit {
    @Input() users: User[] = [];
    @Input() roles: Role[] = [];
    @Input() query: string = '';
    @Input() showDropdown: boolean = false;
    @Output() itemSelected = new EventEmitter<MentionItem>();
    @Output() keyboardNavigation = new EventEmitter<'up' | 'down' | 'enter' | 'escape'>();

    @ViewChild('dropdown') dropdownRef!: ElementRef;

    filteredItems: MentionItem[] = [];
    selectedIndex: number = 0;

    ngOnInit() {
        this.filterItems();
    }

    ngOnChanges() {
        this.filterItems();
        this.selectedIndex = 0;
    }

    private filterItems() {
        const allItems: MentionItem[] = [];

        // Add users
        const userItems: MentionItem[] = this.users.map(user => ({
            type: 'user' as const,
            id: user.id,
            name: user.name,
            email: user.email,
        }));

        // Add roles
        const roleItems: MentionItem[] = this.roles.map(role => ({
            type: 'role' as const,
            name: role.name,
            role: role,
        }));

        allItems.push(...userItems, ...roleItems);

        if (!this.query) {
            this.filteredItems = allItems.slice(0, 10);
        } else {
            this.filteredItems = allItems
                .filter(item => {
                    const searchQuery = this.query.toLowerCase();
                    return (
                        item.name.toLowerCase().includes(searchQuery) ||
                        (item.email && item.email.toLowerCase().includes(searchQuery))
                    );
                })
                .slice(0, 10);
        }
    }

    selectItem(item: MentionItem) {
        this.itemSelected.emit(item);
    }

    navigateUp() {
        this.selectedIndex = Math.max(0, this.selectedIndex - 1);
        this.scrollToSelected();
    }

    navigateDown() {
        this.selectedIndex = Math.min(this.filteredItems.length - 1, this.selectedIndex + 1);
        this.scrollToSelected();
    }

    private scrollToSelected() {
        if (!this.dropdownRef) return;

        const dropdown = this.dropdownRef.nativeElement;
        const selectedItem = dropdown.querySelector(`[data-index="${this.selectedIndex}"]`);

        if (selectedItem) {
            const dropdownRect = dropdown.getBoundingClientRect();
            const itemRect = selectedItem.getBoundingClientRect();

            // Check if item is above the visible area
            if (itemRect.top < dropdownRect.top) {
                selectedItem.scrollIntoView({ block: 'start', behavior: 'smooth' });
            }
            // Check if item is below the visible area
            else if (itemRect.bottom > dropdownRect.bottom) {
                selectedItem.scrollIntoView({ block: 'end', behavior: 'smooth' });
            }
        }
    }

    selectCurrent() {
        if (this.filteredItems[this.selectedIndex]) {
            this.selectItem(this.filteredItems[this.selectedIndex]);
        }
    }
}
